import { queryOptions, useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash-es';
import { z } from 'zod';
import useFriends from '@/hooks/contacts/use-friends';
import { responseScheme } from '@/models/response';
import { type Tag, tagScheme } from '@/models/user/tag';
import { fetcher } from '@/utils/fetcher';

const tagsWithLimitSchema = responseScheme.extend({
  Data: z.object({
    TagMemberLimit: z.number(),
    Tags: tagScheme.array(),
  }),
});

type TagsWithLimitResponseType = z.infer<typeof tagsWithLimitSchema>;

export async function getTags(signal?: AbortSignal): Promise<TagsWithLimitResponseType> {
  return fetcher<TagsWithLimitResponseType>('/GetTagsWithLimit', undefined, {
    signal,
    schema: tagsWithLimitSchema,
    validResponseCodes: [0, 1],
  });
}

export async function getGroupedTagsByMember(signal?: AbortSignal): Promise<Record<number, Tag[]>> {
  const tags = await getTags(signal).then((res) => res.Data?.Tags ?? []);

  const result: Record<number, Tag[]> = {};
  for (const tag of tags) {
    for (const member of tag.TagMember) {
      const memberId = member.Id;
      if (!result[memberId]) result[memberId] = [];
      result[memberId].push(tag);
    }
  }

  return result;
}

export const tagListQueryOptions = queryOptions({
  queryKey: ['/GetTags'],
  queryFn: ({ signal }) => getTags(signal),
  select: (data) => data.Data?.Tags,
  placeholderData: { Code: 0, Msg: '', Data: { TagMemberLimit: 1, Tags: [] } },
});

export default function useTags() {
  const query = useQuery(tagListQueryOptions);

  return {
    tags: query.data ?? [],
    ...query,
  };
}

export function useTagsWithRemarkName() {
  const { friends } = useFriends();

  return useQuery({
    ...tagListQueryOptions,
    select: (data) =>
      data.Data?.Tags.map((tag) => ({
        ...tag,
        TagMember: tag.TagMember.map((member) => {
          const friend = friends[member.Id];
          return friend?.RemarkName ? { ...member, RemarkName: friend.RemarkName } : member;
        }),
      })),
  });
}

export function useTagMemberLimit() {
  return useQuery({
    ...tagListQueryOptions,
    select: (data) => data.Data?.TagMemberLimit,
  });
}

export function useTagsByUserId(userId: Nullable<number>) {
  const query = useQuery({
    ...tagListQueryOptions,
    enabled: !isNil(userId),
    select: (data) => data.Data?.Tags.filter((tag) => tag.TagMember.some((member) => member.Id === userId)) ?? [],
  });

  return {
    tags: query.data ?? [],
    ...query,
  };
}

export function useTagById(tagId: Nullable<number>) {
  return useQuery({
    ...tagListQueryOptions,
    enabled: !isNil(tagId),
    select: (data) => data.Data?.Tags.find((t) => t.Id === tagId) ?? null,
  });
}
